import { aoeDamageMul, roleStats, U } from "./accessors";
import {
  applySwarmApproach,
  clearIntent,
  nearestAllyNeedHeal,
  pickEngageTarget,
} from "./swarm";
import type { InfiniteWarState, IwTeam, IwUnit } from "./types";
import { killUnit } from "./veil";

function crowdMaxChase(): number {
  const c = U.crowd as { maxChaseDistance?: number };
  return c.maxChaseDistance ?? 220;
}

/** Spell / splash / demigod burst damage — respects role `aoeDamageMul`. */
export function applyAoeHit(
  state: InfiniteWarState,
  unit: IwUnit,
  rawDamage: number,
  owner: IwTeam,
): void {
  if (unit.phase !== "alive") return;
  const dmg = rawDamage * aoeDamageMul(unit.roleId);
  unit.hp -= dmg;
  if (unit.hp <= 0) killUnit(state, unit, owner);
}

export function attackDamage(
  unit: IwUnit,
  stats: ReturnType<typeof roleStats>,
  targetHpRatio: number,
): number {
  let dmg = stats.damage * unit.buffMul;
  const executeBelow = (stats as { executeBelow?: number }).executeBelow;
  const executeMul = (stats as { executeMul?: number }).executeMul;
  if (executeBelow != null && executeMul != null && targetHpRatio <= executeBelow) {
    dmg *= executeMul;
  }
  return dmg;
}

export function applyDamage(
  state: InfiniteWarState,
  attacker: IwUnit,
  target: IwUnit,
  damage: number,
  stats: ReturnType<typeof roleStats>,
): void {
  state.events.push({
    kind: "attack",
    team: attacker.team,
    unitId: attacker.id,
    roleId: attacker.roleId,
    x: attacker.x,
    y: attacker.y,
    targetX: target.x,
    targetY: target.y,
  });
  target.hp -= damage;
  const blightSec = (stats as { blightSec?: number }).blightSec;
  const blightDps = (stats as { blightDps?: number }).blightDps;
  if (blightSec) {
    target.blight = Math.max(target.blight, blightSec);
    if (blightDps) target.blight = Math.max(target.blight, blightSec);
  }
  const splash = (stats as { splash?: number }).splash;
  const splashRadius = (stats as { splashRadius?: number }).splashRadius;
  if (splash && splashRadius) {
    for (const nearby of state.units) {
      if (
        nearby === target ||
        nearby.phase !== "alive" ||
        nearby.team === attacker.team ||
        Math.hypot(nearby.x - target.x, nearby.y - target.y) > splashRadius
      ) {
        continue;
      }
      applyAoeHit(state, nearby, damage * splash, attacker.team);
    }
  }
  if (stats.tags.includes("sacrifice")) {
    const rad = (stats as { sacrificeRadius?: number }).sacrificeRadius ?? 40;
    const mul = (stats as { sacrificeBuff?: number }).sacrificeBuff ?? 1.2;
    for (const ally of state.units) {
      if (
        ally.phase !== "alive" ||
        ally.team !== attacker.team ||
        ally.id === attacker.id
      ) {
        continue;
      }
      if (Math.hypot(ally.x - attacker.x, ally.y - attacker.y) <= rad) {
        ally.buffMul = Math.max(ally.buffMul, mul);
      }
    }
  }
  if (target.hp <= 0) killUnit(state, target, attacker.team);
}

export function tickBlight(state: InfiniteWarState, step: number): void {
  for (const unit of state.units) {
    if (unit.phase !== "alive" || unit.blight <= 0) continue;
    unit.blight = Math.max(0, unit.blight - step);
    const dps = 1.0 * aoeDamageMul(unit.roleId);
    unit.hp -= step * dps;
    if (unit.hp <= 0) killUnit(state, unit, "north");
  }
}

export function tickUnits(state: InfiniteWarState, step: number): void {
  for (const unit of state.units) {
    if (unit.phase !== "alive") continue;
    const stats = roleStats(unit.roleId);
    unit.attackCooldown = Math.max(0, unit.attackCooldown - step);
    unit.buffMul = Math.max(1, unit.buffMul - step * 0.15);

    if (stats.tags.includes("heal")) {
      const ally = nearestAllyNeedHeal(state.units, unit, crowdMaxChase());
      if (ally) {
        const { distance } = applySwarmApproach(
          state.units,
          unit,
          ally.x,
          ally.y,
          stats.speed,
        );
        if (distance <= stats.range) {
          clearIntent(unit);
          if (unit.attackCooldown <= 0) {
            unit.attackCooldown = stats.attackSec;
            const heal = (stats as { heal?: number }).heal ?? 3;
            ally.hp = Math.min(ally.maxHp, ally.hp + heal);
            state.events.push({
              kind: "attack",
              team: unit.team,
              unitId: unit.id,
              roleId: unit.roleId,
              x: unit.x,
              y: unit.y,
              targetX: ally.x,
              targetY: ally.y,
              heal: true,
            });
          }
        } else {
          unit.x += unit.intentVx * step;
          unit.y += unit.intentVy * step;
        }
        continue;
      }
    }

    // Forward sticky engage; no unbounded nearest (rear spawn must not yank front press).
    const target = pickEngageTarget(state.units, unit);
    unit.engageTargetId = target?.id ?? null;
    if (!target) {
      const god = state.demigods[unit.team === "south" ? "north" : "south"];
      const { distance } = applySwarmApproach(
        state.units,
        unit,
        god.x,
        god.y,
        stats.speed,
      );
      if (distance > stats.range + 20) {
        unit.x += unit.intentVx * step;
        unit.y += unit.intentVy * step;
        continue;
      }
      clearIntent(unit);
      if (unit.attackCooldown > 0) continue;
      unit.attackCooldown = stats.attackSec;
      const dmg = attackDamage(unit, stats, god.hp / god.maxHp);
      god.hp -= dmg;
      state.events.push({
        kind: "attack",
        team: unit.team,
        unitId: unit.id,
        roleId: unit.roleId,
        x: unit.x,
        y: unit.y,
        targetX: god.x,
        targetY: god.y,
      });
      if (god.hp <= 0) {
        state.winner = unit.team;
        state.events.push({ kind: "win", team: unit.team });
      }
      continue;
    }

    const { distance } = applySwarmApproach(
      state.units,
      unit,
      target.x,
      target.y,
      stats.speed,
    );
    if (distance > stats.range) {
      unit.x += unit.intentVx * step;
      unit.y += unit.intentVy * step;
      continue;
    }
    clearIntent(unit);
    if (unit.attackCooldown > 0) continue;
    unit.attackCooldown = stats.attackSec;
    const dmg = attackDamage(unit, stats, target.hp / target.maxHp);
    applyDamage(state, unit, target, dmg, stats);
  }
}
